import React, { useState, useMemo } from 'react';
import {
  Button,
  Checkbox,
  FullscreenDialog,
  MenuItem,
  Select,
  Text,
  VSpacer,
} from '@/components/DesignSystem';
import {
  Box,
  Chip,
  FormControlLabel,
  IconButton,
  InputAdornment,
  ListSubheader,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  useTheme,
} from '@mui/material';
import CancelIcon from '@mui/icons-material/Cancel';
import FilterListIcon from '@mui/icons-material/FilterList';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import RefreshIcon from '@mui/icons-material/Refresh';
import { ExpandableFilterBox } from './ExpandableFilterBox';
import { CustomerFilterChip } from './CustomerFilterChip';
import { FilterOptions } from '@shared/interfaces/ScheduledNotification';
import { RetailerApi } from '@/utilities/api/RetailerApi';
import { useQuery } from 'react-query';
import { QueryKeys } from '@/constants/QueryKeys';
import { useTranslation } from 'react-i18next';
import { ProgramPeriodSelector } from '@/pages/RetailerOverview/ProgramPeriodSelector';
import { getProgramPeriodLabel } from '@/pages/RetailerOverview/helpers';
import { SubwalletApi } from '@/utilities/api/SubwalletApi';
import { PromotionApi } from '@/utilities/api/PromotionApi';
import { businessToday } from '@shared/utilities';
import { agronomyProgramsOnly } from '@/utilities/RewardsProgramUtility';

interface CustomerFilterModalProps {
  initialFilters?: FilterOptions,
  onApplyFilter: (filters: FilterOptions) => void,
  onClose: () => void,
  open: boolean,
  retailerId: string,
  testID?: string,
}

export const CustomerFilterModal: React.FC<CustomerFilterModalProps> = ({
  initialFilters,
  onApplyFilter,
  onClose,
  open,
  retailerId,
  testID = 'customer-filter-modal',
}) => {
  const { i18n, t } = useTranslation();
  const theme = useTheme();
  const { data: retailerData } = useQuery(
    [QueryKeys.GET_RETAILER_BY_ID, retailerId],
    () => RetailerApi.get(retailerId),
  );

  // Create a map of tier ranks to tier names
  const tierMap = useMemo(() => {
    const map: Record<number, string> = {};

    const [program] = agronomyProgramsOnly(retailerData?.rewardsPrograms);
    program?.tiers?.forEach(tier => {
      map[tier.rank] = tier.name;
    });

    return map;
  }, [retailerData]);

  const rewardsPrograms = useMemo(
    () => agronomyProgramsOnly(retailerData?.rewardsPrograms),
    [retailerData],
  );

  const { data: subwallets } = useQuery(
    [QueryKeys.LIST_SUBWALLETS, retailerId],
    () => SubwalletApi.list({ retailerId }),
  );

  const { data: promotionsData } = useQuery(
    [QueryKeys.GET_PROMOTIONS_FOR_RETAILER, retailerId],
    () => PromotionApi.listForRetailer({ retailerId, includeExpired: true }),
  );

  const isTierSelected = (rank: number): boolean => {
    return currentRanks.includes(rank);
  };

  const [currentRanks, setCurrentRanks] = useState<number[]>(
    initialFilters?.currentRanks ?? [],
  );
  const [
    hasReEarnedCurrentTierStatus,
    setHasReEarnedCurrentTierStatus,
  ] = useState<boolean | null>(
    initialFilters?.hasReEarnedCurrentTierStatus !== undefined
      ? initialFilters.hasReEarnedCurrentTierStatus as boolean
      : null,
  );
  const [minPoints, setMinPoints] = useState<number | null>(
    initialFilters?.minPoints !== undefined
      ? initialFilters.minPoints as number
      : null,
  );
  const [maxPoints, setMaxPoints] = useState<number | null>(
    initialFilters?.maxPoints !== undefined
      ? initialFilters.maxPoints as number
      : null,
  );
  const [pointsRangeError, setPointsRangeError] = useState<string | null>(null);

  const [selectedProgramYearId, setSelectedProgramYearId] = useState<string | null>(
    initialFilters?.rewardsProgramId ?? null,
  );

  const selectedProgramYear = useMemo(
    () => rewardsPrograms.find((program) => program.id === selectedProgramYearId) ?? null,
    [rewardsPrograms, selectedProgramYearId],
  );

  const [selectedSubwalletIds, setSelectedSubwalletIds] = useState<string[]>(
    initialFilters?.subwalletIds as string[] || [],
  );

  const [selectedExpirationDate, setSelectedExpirationDate] = useState<string | null>(
    initialFilters?.pointsExpiringOnDate ?? null,
  );

  const [isExpirationDateExpanded, setIsExpirationDateExpanded] = useState<boolean>(false);
  const [isTierRankExpanded, setIsTierRankExpanded] = useState<boolean>(false);
  const [isPointsRangeExpanded, setIsPointsRangeExpanded] = useState<boolean>(false);
  const [isProgramYearExpanded, setIsProgramYearExpanded] = useState<boolean>(false);
  const [isSubwalletExpanded, setIsSubwalletExpanded] = useState<boolean>(false);
  const [isReEarnedExpanded, setIsReEarnedExpanded] = useState<boolean>(false);

  const retailerSubwallets = useMemo(
    () => subwallets?.filter(sw => sw.manufacturerId === null),
    [subwallets],
  );
  const manufacturerSubwallets = useMemo(
    () => subwallets?.filter(sw => sw.manufacturerId !== null),
    [subwallets],
  );

  const expirationDateOptions = useMemo(() => {
    const today = businessToday();
    const promotionDates = (promotionsData?.data ?? [])
      .map((p) => p.pointsExpirationDate)
      .filter((d): d is string => !!d && d > today);
    const programDates = rewardsPrograms
      .map((p) => p.pointsExpirationDate)
      .filter((d): d is string => !!d && d > today);
    const uniqueDates = [...new Set([...promotionDates, ...programDates])].sort();
    return uniqueDates.map((value) => ({
      label: new Intl.DateTimeFormat(i18n.language, {
        day: 'numeric',
        month: 'short',
        timeZone: 'UTC',
        year: 'numeric',
      }).format(new Date(value)),
      value,
    }));
  }, [i18n.language, promotionsData, rewardsPrograms]);

  const handleReset = () => {
    setCurrentRanks([]);
    setIsTierRankExpanded(false);
    setHasReEarnedCurrentTierStatus(null);
    setIsReEarnedExpanded(false);
    setMinPoints(null);
    setMaxPoints(null);
    setPointsRangeError(null);
    setIsPointsRangeExpanded(false);
    setSelectedProgramYearId(null);
    setIsProgramYearExpanded(false);
    setSelectedSubwalletIds([]);
    setIsSubwalletExpanded(false);
    setSelectedExpirationDate(null);
    setIsExpirationDateExpanded(false);
  };

  const handleApplyFilters = () => {
    if (subwalletRequiresMinPoints) {
      return;
    }
    const filtersOption: FilterOptions = {};

    if (currentRanks.length > 0) {
      filtersOption.currentRanks = currentRanks;
    }

    if (hasReEarnedCurrentTierStatus !== null) {
      filtersOption.hasReEarnedCurrentTierStatus = hasReEarnedCurrentTierStatus;
    }

    if (minPoints !== null && maxPoints !== null) {
      if (maxPoints > minPoints) {
        filtersOption.minPoints = minPoints;
        filtersOption.maxPoints = maxPoints;
        setPointsRangeError(null);
      } else {
        const errorMsg = t(
          $ => $.notificationBuilder.modal.customerFilter.validation.maxGreaterThanMin,
        );
        setPointsRangeError(errorMsg);
        return;
      }
    } else {
      if (minPoints !== null) {
        filtersOption.minPoints = minPoints;
      }
      if (maxPoints !== null) {
        filtersOption.maxPoints = maxPoints;
      }
      setPointsRangeError(null);
    }

    if (selectedProgramYearId !== null) {
      filtersOption.rewardsProgramId = selectedProgramYearId;
    }

    if (selectedSubwalletIds.length > 0) {
      filtersOption.subwalletIds = selectedSubwalletIds;
    }

    if (selectedExpirationDate !== null) {
      filtersOption.pointsExpiringOnDate = selectedExpirationDate;
    }

    if (getHasFiltersSelected()) {
      onApplyFilter(filtersOption);
    } else {
      onApplyFilter({});
    }
    onClose();
  };

  const getHasFiltersSelected = () => {
    return (
      currentRanks.length > 0 ||
      hasReEarnedCurrentTierStatus !== null ||
      minPoints !== null ||
      maxPoints !== null ||
      selectedProgramYearId !== null ||
      selectedSubwalletIds.length > 0 ||
      selectedExpirationDate !== null
    );
  };

  const subwalletRequiresMinPoints = selectedSubwalletIds.length > 0 && minPoints === null;

  const getHasNonProgramYearFilterSelected = () => {
    return (
      currentRanks.length > 0 ||
      hasReEarnedCurrentTierStatus !== null ||
      minPoints !== null ||
      maxPoints !== null ||
      selectedSubwalletIds.length > 0 ||
      selectedExpirationDate !== null
    );
  };

  const filterCount = (
    (currentRanks.length > 0 ? 1 : 0) +
    (hasReEarnedCurrentTierStatus !== null ? 1 : 0) +
    (minPoints !== null || maxPoints !== null ? 1 : 0) +
    (selectedProgramYearId !== null ? 1 : 0) +
    (selectedSubwalletIds.length > 0 ? 1 : 0) +
    (selectedExpirationDate !== null ? 1 : 0)
  );

  return (
    <FullscreenDialog
      hideCloseButton
      hideTitleBorder
      open={open}
      sx={{
        backgroundColor: '#F7F7F7',
        '& .MuiDialogContent-root': {
          maxWidth: '100% !important',
        },
      }}
      testID={testID}
    >
      <Box sx={{ maxWidth: '720px', margin: '0 auto', width: '100%' }}>
      <Stack>
        <Stack
          alignItems="center"
          direction="row"
          justifyContent="space-between"
          sx={{ width: '100%' }}
        >
          <Text category="headline-medium">
            {t($ => $.notificationBuilder.modal.customerFilter.title)}
          </Text>
          <Stack alignItems="center" direction="row" sx={{ gap: '16px' }}>
            <Button
              onClick={() => onClose()}
              testID={`${testID}-cancel-button`}
              variant="outlined"
            >
              {t($ => $.common.actions.cancel)}
            </Button>
            <Button
              color="primary"
              disabled={
                !getHasNonProgramYearFilterSelected()
                || pointsRangeError !== null
                || subwalletRequiresMinPoints
              }
              onClick={handleApplyFilters}
              testID={`${testID}-generate-list-button`}
              variant="contained"
            >
              {t($ => $.notificationBuilder.modal.customerFilter.generateRecipients)}
            </Button>
          </Stack>
        </Stack>
      </Stack>
      <VSpacer size="5" />
      <Stack>
        <Text category="body-medium">
          {t($ => $.notificationBuilder.modal.customerFilter.specifyCriteria)}
        </Text>
      </Stack>
      <VSpacer size="8" />
      <Stack>
        <Stack alignItems="center" direction="row" justifyContent="space-between">
          <Stack alignItems="center" direction="row" spacing={1}>
            <FilterListIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
            <Text category="body-medium" sx={{ color: 'text.secondary' }}>
              {t($ =>
                $.notificationBuilder.modal.customerFilter.filtersApplied, { count: filterCount })
              }
            </Text>
          </Stack>
          <Button
            disabled={!filterCount}
            onClick={handleReset}
            startIcon={<RefreshIcon />}
            testID={`${testID}-reset-button`}
            variant="text"
          >
            {t($ => $.notificationBuilder.modal.customerFilter.reset)}
          </Button>
        </Stack>
        <VSpacer size="3" />
        <Stack spacing={1}>
          <Text category="body-small" sx={{ color: 'text.secondary' }}>
            {t($ => $.notificationBuilder.modal.customerFilter.programYearDescription)}
          </Text>
          <ExpandableFilterBox
            isExpanded={isProgramYearExpanded}
            onToggle={() => setIsProgramYearExpanded(!isProgramYearExpanded)}
            summaryContent={
              selectedProgramYear !== null ? (
                <CustomerFilterChip
                  label={getProgramPeriodLabel(selectedProgramYear)}
                  onDelete={() => setSelectedProgramYearId(null)}
                />
              ) : undefined
            }
            title={t($ => $.notificationBuilder.modal.customerFilter.filters.programYear)}
          >
            <ProgramPeriodSelector
              onSelectProgram={(program) => setSelectedProgramYearId(program.id)}
              programs={rewardsPrograms}
              selectedProgram={selectedProgramYear ?? undefined}
              showAsDropdown
            />
          </ExpandableFilterBox>
        </Stack>
        <VSpacer size="5" />
        <Stack spacing={1} sx={{ mb: 2 }}>
          <Text category="body-small" sx={{ color: 'text.secondary' }}>
            {t($ => $.notificationBuilder.modal.customerFilter.filtersDescription)}
          </Text>
        </Stack>
        <Stack spacing={2}>
          <ExpandableFilterBox
            isExpanded={isTierRankExpanded}
            onToggle={() => setIsTierRankExpanded(!isTierRankExpanded)}
            summaryContent={
              currentRanks.length > 0 ? (
                <Stack alignItems="center" direction="row" spacing={0.5}>
                  {currentRanks.map((currentRank) => (
                    <CustomerFilterChip
                      key={currentRank}
                      label={tierMap[currentRank]}
                      onDelete={() => {
                        setCurrentRanks(prev => prev.filter(r => r !== currentRank));
                      }}
                    />
                  ))}
                </Stack>
              ) : undefined
            }
            title={t($ => $.notificationBuilder.modal.customerFilter.filters.currentStatus)}
          >
            <Box sx={{ display: 'flex', flexWrap: 'wrap' }}>
              {Object.entries(tierMap)
                .map(([rank, name]) => {
                  const currentRank = Number(rank);
                  const selected = isTierSelected(currentRank);
                  return (
                    <Box key={rank} sx={{ m: 0.5 }}>
                      <Chip
                        label={name}
                        onClick={() => {
                          if (selected) {
                            setCurrentRanks(prev => prev.filter(r => r !== currentRank));
                          } else {
                            setCurrentRanks([...currentRanks, currentRank]);
                          }
                        }}
                        sx={(theme) => ({
                          height: '32px',
                          fontSize: '14px',
                          fontWeight: 500,
                          backgroundColor: selected ? theme.palette.primary.main : 'white',
                          color: selected ? theme.palette.primary.contrastText : 'inherit',
                          border: selected
                            ? `1px solid ${theme.palette.primary.main}`
                            : '1px solid var(--base-outline, #E0E0E0)',
                          '&:hover': {
                            backgroundColor: selected
                              ? theme.palette.primary.dark
                              : '#f5f5f5',
                          },
                        })}
                      />
                    </Box>
                  );
                })}
            </Box>
          </ExpandableFilterBox>

          <ExpandableFilterBox
            isExpanded={isReEarnedExpanded}
            onToggle={() => setIsReEarnedExpanded(!isReEarnedExpanded)}
            summaryContent={
              hasReEarnedCurrentTierStatus !== null ? (
                <CustomerFilterChip
                  label={
                    hasReEarnedCurrentTierStatus
                      ? t($ => $.notificationBuilder.modal.customerFilter.filters.hasReEarned)
                      : t($ => $.notificationBuilder.modal.customerFilter.filters.hasNotReEarned)
                  }
                  onDelete={() => setHasReEarnedCurrentTierStatus(null)}
                />
              ) : undefined
            }
            title={t($ => $.notificationBuilder.modal.customerFilter.filters.reEarnedTier)}
          >
            <RadioGroup
              onChange={(e) => setHasReEarnedCurrentTierStatus(e.target.value === 'true')}
              sx={{ marginLeft: "20px" }}
              value={
                hasReEarnedCurrentTierStatus === null
                  ? ''
                  : String(hasReEarnedCurrentTierStatus)
              }
            >
              <FormControlLabel
                control={<Radio />}
                label={t($ => $.notificationBuilder.modal.customerFilter.filters.hasReEarned)}
                sx={{ mb: 1 }}
                value="true"
              />
              <FormControlLabel
                control={<Radio />}
                label={t(
                  $ => $.notificationBuilder.modal.customerFilter.filters.hasNotReEarned,
                )}
                value="false"
              />
            </RadioGroup>
          </ExpandableFilterBox>

          <ExpandableFilterBox
            isExpanded={isPointsRangeExpanded}
            onToggle={() => setIsPointsRangeExpanded(!isPointsRangeExpanded)}
            summaryContent={
              minPoints !== null || maxPoints !== null ? (
                <Stack alignItems="center" direction="row" spacing={0.5}>
                  {minPoints !== null && (
                    <CustomerFilterChip
                      label={`${t($ => $.common.filter.min)} ${minPoints}`}
                      onDelete={() => {
                        setMinPoints(null);
                        setPointsRangeError(null);
                      }}
                    />
                  )}
                  {maxPoints !== null && (
                    <CustomerFilterChip
                      label={`${t($ => $.common.filter.max)} ${maxPoints}`}
                      onDelete={() => {
                        setMaxPoints(null);
                        setPointsRangeError(null);
                      }}
                    />
                  )}
                </Stack>
              ) : undefined
            }
            title={
              t(($) => $.notificationBuilder.modal.customerFilter.filters.hasPointsWithinRange)
            }
          >
            <Stack direction="column" spacing={2}>
              <Stack direction="row" spacing={2} sx={{ alignItems: 'center' }}>
                <TextField
                  InputProps={{
                    endAdornment: minPoints !== null && (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="clear min points"
                          edge="end"
                          onClick={(e) => {
                            e.stopPropagation();
                            setMinPoints(null);
                            setPointsRangeError(null);
                          }}
                        >
                          <CancelIcon />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  error={pointsRangeError !== null}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (value === '') {
                      setMinPoints(null);
                      setPointsRangeError(null);
                    } else {
                      const numValue = parseInt(value, 10);
                      if (!isNaN(numValue)) {
                        setMinPoints(numValue);
                        if (maxPoints !== null && numValue >= maxPoints) {
                          const errorMsg = t(
                            $ => $.notificationBuilder.modal.customerFilter
                              .validation.maxGreaterThanMin,
                          );
                          setPointsRangeError(errorMsg);
                        } else {
                          setPointsRangeError(null);
                        }
                      }
                    }
                  }}
                  placeholder={t(($) => $.common.filter.min)}
                  sx={{ width: '50%' }}
                  type="number"
                  value={minPoints === null ? '' : minPoints}
                  variant="outlined"
                />
                <TextField
                  InputProps={{
                    endAdornment: maxPoints !== null && (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="clear max points"
                          edge="end"
                          onClick={(e) => {
                            e.stopPropagation();
                            setMaxPoints(null);
                            setPointsRangeError(null);
                          }}
                        >
                          <CancelIcon />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  error={pointsRangeError !== null}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (value === '') {
                      setMaxPoints(null);
                      setPointsRangeError(null);
                    } else {
                      const numValue = parseInt(value, 10);
                      if (!isNaN(numValue)) {
                        setMaxPoints(numValue);
                        if (minPoints !== null && numValue <= minPoints) {
                          const errorMsg = t(
                            $ => $.notificationBuilder.modal.customerFilter
                              .validation.maxGreaterThanMin,
                          );
                          setPointsRangeError(errorMsg);
                        } else {
                          setPointsRangeError(null);
                        }
                      }
                    }
                  }}
                  placeholder={t(($) => $.common.filter.max)}
                  sx={{ width: '50%' }}
                  type="number"
                  value={maxPoints === null ? '' : maxPoints}
                  variant="outlined"
                />
              </Stack>
              {pointsRangeError && (
                <Text category="body-small" color="error">
                  {pointsRangeError}
                </Text>
              )}
              <Stack alignItems="center" direction="row" spacing={0.5}>
                <InfoOutlinedIcon sx={{ color: 'text.secondary', fontSize: 16 }} />
                <Text category="body-small" sx={{ color: 'text.secondary' }}>
                  Compared against the sum of available (non-reserved, non-expired) points
                  across all selected subwallets.
                </Text>
              </Stack>
            </Stack>
          </ExpandableFilterBox>

          <ExpandableFilterBox
            isExpanded={isSubwalletExpanded}
            onToggle={() => setIsSubwalletExpanded(!isSubwalletExpanded)}
            summaryContent={
              selectedSubwalletIds.length > 0 ? (
                <Stack
                  alignItems="center"
                  direction="row"
                  spacing={0.5}
                  sx={{
                    flexWrap: 'wrap',
                    justifyContent: 'flex-end',
                    maxWidth: '100%',
                  }}
                  useFlexGap
                >
                  {selectedSubwalletIds.map((subwalletId) => {
                    const subwallet = subwallets?.find((sw) => sw.id === subwalletId);
                    return (
                      <CustomerFilterChip
                        key={subwalletId}
                        label={subwallet?.name ?? subwalletId}
                        onDelete={() => setSelectedSubwalletIds(
                          (prev) => prev.filter((id) => id !== subwalletId),
                        )}
                      />
                    );
                  })}
                </Stack>
              ) : undefined
            }
            title={
              t($ => $.notificationBuilder.modal.customerFilter.filters.hasPointsInSubwallets)
            }
          >
            <Select
              displayEmpty
              multiple
              renderValue={() => selectedSubwalletIds.length > 0 ? (
                <Text category="body-large">
                  {`${selectedSubwalletIds.length} selected`}
                </Text>
              ) : (
                <Text category="body-large" sx={{ color: theme.palette.text.secondary }}>
                  Select subwallets
                </Text>
              )}
              sx={{ width: '100%' }}
              testID="subwallet-multi-select"
              value={[]}
              variant="outlined"
            >
              {!!retailerSubwallets && retailerSubwallets.length > 0 && (
                <ListSubheader sx={{ paddingLeft: '16px' }}>Retailer</ListSubheader>
              )}
              {retailerSubwallets?.map((sw) => (
                <MenuItem
                  key={sw.id}
                  onClick={() => setSelectedSubwalletIds((prev) =>
                    prev.includes(sw.id)
                      ? prev.filter((id) => id !== sw.id)
                      : [...prev, sw.id],
                  )}
                  sx={{ paddingY: 0.5 }}
                  testID={`subwallet-option-${sw.id}`}
                >
                  <Checkbox
                    checked={selectedSubwalletIds.includes(sw.id)}
                    testID={`subwallet-checkbox-${sw.id}`}
                  >
                    <Text category="body-large">{sw.name}</Text>
                  </Checkbox>
                </MenuItem>
              ))}
              {!!manufacturerSubwallets && manufacturerSubwallets.length > 0 && (
                <ListSubheader sx={{ paddingLeft: '16px' }}>Manufacturer</ListSubheader>
              )}
              {manufacturerSubwallets?.map((sw) => (
                <MenuItem
                  key={sw.id}
                  onClick={() => setSelectedSubwalletIds((prev) =>
                    prev.includes(sw.id)
                      ? prev.filter((id) => id !== sw.id)
                      : [...prev, sw.id],
                  )}
                  sx={{ paddingY: 0.5 }}
                  testID={`subwallet-option-${sw.id}`}
                >
                  <Checkbox
                    checked={selectedSubwalletIds.includes(sw.id)}
                    testID={`subwallet-checkbox-${sw.id}`}
                  >
                    <Text category="body-large">{sw.name}</Text>
                  </Checkbox>
                </MenuItem>
              ))}
            </Select>
            <Stack alignItems="center" direction="row" spacing={0.5} sx={{ mt: 1 }}>
              <InfoOutlinedIcon sx={{ color: 'text.secondary', fontSize: 16 }} />
              <Text category="body-small" sx={{ color: 'text.secondary' }}>
                Min/max points filter applies to the sum of points in the selected subwallets.
              </Text>
            </Stack>
            {subwalletRequiresMinPoints && (
              <Text category="body-small" color="error" sx={{ mt: 0.5 }}>
                A minimum value in &quot;Has points within range&quot; is required when subwallets
                are selected.
              </Text>
            )}
          </ExpandableFilterBox>

          <ExpandableFilterBox
            isExpanded={isExpirationDateExpanded}
            onToggle={() => setIsExpirationDateExpanded(!isExpirationDateExpanded)}
            summaryContent={
              selectedExpirationDate !== null ? (
                <CustomerFilterChip
                  label={
                    expirationDateOptions.find((o) => o.value === selectedExpirationDate)?.label
                    ?? selectedExpirationDate
                  }
                  onDelete={() => setSelectedExpirationDate(null)}
                />
              ) : undefined
            }
            title={t($ => $.notificationBuilder.modal.customerFilter.filters.pointsExpiringOnDate)}
          >
            <Select
              displayEmpty
              onChangeValue={(value) => setSelectedExpirationDate(value as string)}
              renderValue={() => selectedExpirationDate ? (
                <Text category="body-large">
                  {expirationDateOptions.find((o) => o.value === selectedExpirationDate)?.label}
                </Text>
              ) : (
                <Text category="body-large" sx={{ color: 'text.secondary' }}>
                  {t($ => $.notificationBuilder.modal.customerFilter.filters.selectExpirationDate)}
                </Text>
              )}
              sx={{ width: '100%' }}
              testID="expiration-date-select"
              value={selectedExpirationDate ?? ''}
              variant="outlined"
            >
              {expirationDateOptions.map((option) => (
                <MenuItem
                  key={option.value}
                  testID={`expiration-date-${option.value}`}
                  value={option.value}
                >
                  <Text category="body-large">{option.label}</Text>
                </MenuItem>
              ))}
            </Select>
          </ExpandableFilterBox>

          <Box sx={{ height: '16px' }} />
        </Stack>
      </Stack>
      </Box>
    </FullscreenDialog>
  );
};
